import React, { useEffect, useState } from 'react';
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router";

import ExternalLink from '@assets/images/external_link.svg';

import Spinner from '@components/spinners/Circular/Circular';
import { NavBar } from "@components/navigation/NavBar/NavBar";
import WarningBox from '@components/cards/messageBox/WarningBox';
import Button, { ButtonStyle } from '@components/buttons/Button/Button';
import CustomerPotential from '@components/cards/CustomerPotential/CustomerPotential';

import { UserFormStates, useUserJourneyContext } from "@context/UserJourneyContext";
import useServiceHook from "@hooks/useServiceHook";
import getCustomerPotentials, { ICustomerPotential } from '@services/customer/CustomerPotentials';

import { Country } from "@utils/constants/localisation";
import { getZohoContactLink, getZohoCreatePotentialLink } from "@utils/misc/functions";


function checkIfQuoteSigned(quoteVersion?: Number, quoteStatus?: string) {
    if (quoteVersion == 2){
        return (quoteStatus === 'completed');
    } else {
        return true; // For FO1.0 and FT1.0, allow all potentials
    }
}

export default function SelectPotentialView() {
    const navigate = useNavigate();
    const { custId, country } = useParams() as { custId: string; country: Country; };
    const [refreshTrigger, setRefreshTrigger] = useState<boolean>(false);
    const filter_closed_potentials = "true";
    const { updateFormState } = useUserJourneyContext();
    const [loading, , , allPotentials] = useServiceHook(
        getCustomerPotentials,
        [custId, country],
        true,
        [refreshTrigger],
        undefined,
        [filter_closed_potentials]  // Optional param to not show closed potentials
    );
    
    const WarningMessages = {
        NoOpenPotentials: <>No open Potential could be found for this customer. Please create a Potential and try again.</>,
        AUOpenPotentials: <>The customer has open potentials, but they must be in the stage <strong>'Onboarding Booked'</strong> or <strong>'Setup Fee Paid'</strong> to continue.</>,
        GBOpenPotentials: <>The customer has open potentials, but they must be in the stage <strong>'Setup Fee Paid - Awaiting Vehicle'</strong> or <strong>'Setup Fee Paid'</strong> to continue.</>
    } as const;

    const [filteredPotentials, setFilteredPotentials] = useState<ICustomerPotential[]>([]);

    const zoho_potentials_url = getZohoCreatePotentialLink(country);
    const zoho_contact_url = getZohoContactLink(country, custId);

    const [showWarning, setShowWarning] = useState<boolean>(false);
    const [warningText, setWarningText] = useState<JSX.Element>(WarningMessages.NoOpenPotentials);
    const [showViewPotentialButton, setShowViewPotentialButton] = useState<boolean>(false);
    const potentialStagesAU = ['Onboarding Booked', 'Setup Fee Paid'];
    const potentialStagesGB = ['Setup Fee Paid - Awaiting Vehicle', 'Setup Fee Paid']

    // The purpose of this useEffect is to show whether the customer has any open potentials and whether they are in the right stage.
    // The customer must have a potential that is included in the potentialStages list in order to select a potential to onboard into the vehicle. 
    // If the customer has open potentials but in the wrong stage the CSR should be prompted with a message explaining which stages the potential 
    // has to be in to progress. If the customer has open potentials in the correct stage then the potential will be shown and the warning message
    // will not.
    useEffect(() => {
        if (!allPotentials) return;

        if (allPotentials.length === 0) {
            setWarningText(WarningMessages.NoOpenPotentials);
            setShowWarning(true);
            return;
        }
        const potentialStages = country == "AU" ? potentialStagesAU : potentialStagesGB

        const potentialsInRightStage = filterPotentialsForCorrectStage(allPotentials, potentialStages);
        setFilteredPotentials(potentialsInRightStage);

        if (potentialsInRightStage.length === 0 && allPotentials.length > 0) {
            setWarningText(
                country === "AU"
                    ? WarningMessages.AUOpenPotentials
                    : WarningMessages.GBOpenPotentials
            );
            setShowWarning(true);
            setShowViewPotentialButton(true);
        } else {
            setShowWarning(false);
        }
    }, [allPotentials]);

    useEffect(() => {
        // Refresh potentials every 5 seconds (if not already loading)
        // recreates the interval every time loading changes
        const interval = setInterval(() => {
            if (!loading) {
                setRefreshTrigger(prev => !prev);
            }
        }, 5000);
        return () => clearInterval(interval);
    }, [loading]);


    useEffect(() => {
        // Clear the selected potential whenever we render this component.
        // This is to prevent the user from navigating from this page with
        // an old potential selected.
        const empty_potential: ICustomerPotential = {
            id: '',
            stage: '',
            vehicle: '',
            plan: '',
            created_date: '',
            zoho_contact_name: ''
        };
        updateFormState(UserFormStates.selectedPotential, empty_potential);
    }, []);

    const onCreatePotentialClick = () => window.open(zoho_potentials_url, '_blank');
    const onViewPotentialClick = () => window.open(zoho_contact_url, '_blank');

    const onPotentialClick = (potential: ICustomerPotential) => {
        // AU: only allow v2 quotes that are signed. Non-AU: allow if quote has a status.
        if (country === "AU") {
            if (!checkIfQuoteSigned(potential.quote_version, potential.quote_status)) {
                return;
            }
        } else if (potential.quote_version == 2 && !potential.quote_status) {
            return;
        }

        // When a potential is selected, update the form state and
        // navigate to the next page of onboard journey
        updateFormState(UserFormStates.selectedPotential, potential);

        // At this time it is agreed to route only FOv2 journeys differently.
        // This is susceptible to be changed.
        if (potential?.quote_version == 2){
            navigate(`/onboarding/v2/${country}/${custId}/view`);
        } else {
            navigate(`/onboarding/${country}/${custId}/vehicle-assignment`);
        }
    };

    const filterPotentialsForCorrectStage = (potentials: ICustomerPotential[], potentialStages: string[]) =>
        potentials.filter(potential => potentialStages.includes(potential.stage));

    return (<>
        <NavBar pageTitle={"Select Potential"} />
        <p style={{ margin: '2.5vh 0' }}>Please confirm the Potential for the new onboard:</p>
        {showWarning ? (
            <div style={{ margin: '2.5vh 0' }}>
                <WarningBox message={warningText} />
            </div>
        ) : (
            <ul style={{ padding: 0, listStyleType: 'none' }}>
                {filteredPotentials.map((potential: ICustomerPotential, index: number) => {
                    // For FO2.0 and FT 2.0, restrict selection of Potential only to signed quotes
                    const isClickable = country === "AU"
                        ? checkIfQuoteSigned(potential.quote_version, potential.quote_status)
                        : !(potential.quote_version == 2 && !potential.quote_status);

                    return (
                        <li key={potential.id}
                            onClick={() => onPotentialClick(potential)}
                            style={{
                                opacity: !isClickable ? 0.5 : 1,
                                cursor: !isClickable ? 'default' : 'pointer',
                            }}>
                            <div style={{
                                width: '100%',
                                height: '1px',
                                backgroundColor: '#ccc',
                                marginBottom: '10px'
                            }}></div>
                            <CustomerPotential
                                potential={potential}
                                country={country}
                            />
                        </li>
                    )
                })}
                <div style={{ width: '100%', height: '1px', backgroundColor: '#ccc', marginTop: '10px' }}></div>
            </ul>
        )}

        {showViewPotentialButton ? (
            <Button
                label='View Potentials'
                onClickFunc={onViewPotentialClick}
                iconSrc={ExternalLink}
                btnType={ButtonStyle.primary}
                iconRight={true}
            />
        ) : (
            <Button
                label='Create Potential'
                onClickFunc={onCreatePotentialClick}
                iconSrc={ExternalLink}
                btnType={ButtonStyle.primary}
                iconRight={true}
            />
        )}

        {loading && <LoadingInformation />}

    </>);
};


const LoadingInformation = () => (
    <div style={{ display: 'flex', justifyContent: 'center', alignContent: 'right' }}>
        <div style={{ margin: '5vh' }}>
            <Spinner testId="spinner" />
        </div>
        <div style={{ alignContent: 'center' }}>
            <p>Loading potentials...</p>
        </div>
    </div>
);